import type { DependencyList } from 'react';

import isEqual from 'fast-deep-equal/react';

import { useCompareCallback } from './compare-callback.hook';

/**
 * A hook that returns a memoized callback function that compares dependencies using deep equality.
 *
 * @param callback - The callback function to be memoized.
 * @param dependencies - An array of dependencies that, when changed, will cause the callback to be recomputed.
 * @returns A memoized version of the callback function.
 *
 * @example
 * const memoizedCallback = useDeepCompareCallback(() => {
 *   // Callback logic here
 * }, [dependency1, dependency2]);
 */
export const useDeepCompareCallback = <TFunction extends (...args: unknown[]) => unknown>(
  callback: TFunction,
  dependencies: DependencyList
): TFunction => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are memoized using useCompareCallback
  return useCompareCallback(callback, dependencies, isEqual);
};
